import { LineCopy } from '@qr-front/icons/line-copy'
import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { inputCls } from '@qr-front/ui/components/Input'
import { cn } from '@qr-front/ui/utils'
import { DialogPortal } from '@radix-ui/react-dialog'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'
import * as React from 'react'

import { showNotification } from '@/components/Notification'
import { copyToClipboard, selectElementContents } from '@/utils'

import { PHONE_PREVIEW_CONTENT_ID } from '../../../components/PhonePreview/PhonePreview'
import { qrUriAtom } from '../generatorState'
import { buildQrLandingUrl } from '../utils/buildQrLandingUrl'
import { SOCIAL_PLATFORM_ICONS } from '../utils/socialPlatformsConfig'

const ShareDialogPortal = ({ children }: { children: ReactNode }) => {
	const [container, setContainer] = React.useState<HTMLElement | null>(null)

	React.useEffect(() => {
		if (container?.isConnected) return

		const candidates = Array.from(
			document.querySelectorAll<HTMLElement>(`#${PHONE_PREVIEW_CONTENT_ID}`),
		)
		setContainer(
			candidates.find((el) => el.offsetParent !== null) ?? candidates[0] ?? null,
		)
	}, [container])

	if (!container) return null

	return <DialogPortal container={container}>{children}</DialogPortal>
}

const FacebookIcon = SOCIAL_PLATFORM_ICONS['Facebook']

const FacebookShareButton = ({
	qrLandingUrl,
	previewMode,
}: {
	qrLandingUrl: string
	previewMode?: boolean
}) => (
	<a
		href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(qrLandingUrl)}`}
		target="_blank"
		rel="noopener noreferrer"
	>
		<FacebookIcon className={cn('size-[60px]', previewMode && 'size-[40px]')} />
	</a>
)

const LinkedinIcon = SOCIAL_PLATFORM_ICONS['Linkedin']

const LinkedinShareButton = ({
	qrLandingUrl,
	previewMode,
}: {
	qrLandingUrl: string
	previewMode?: boolean
}) => (
	<a
		href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(qrLandingUrl)}`}
		target="_blank"
		rel="noopener noreferrer"
	>
		<LinkedinIcon className={cn('size-[60px]', previewMode && 'size-[40px]')} />
	</a>
)

const XIcon = SOCIAL_PLATFORM_ICONS['Twitter']

const XShareButton = ({
	qrLandingUrl,
	previewMode,
}: {
	qrLandingUrl: string
	previewMode?: boolean
}) => (
	<a
		href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(qrLandingUrl)}`}
		target="_blank"
		rel="noopener noreferrer"
	>
		<XIcon className={cn('size-[60px]', previewMode && 'size-[40px]')} />
	</a>
)

export const ShareQrDialog = ({
	isOpen,
	setIsOpen,
	previewMode,
}: {
	isOpen: boolean
	setIsOpen: (open: boolean) => void
	previewMode?: boolean
}) => {
	const t = useTranslations()
	const [isCopied, setIsCopied] = React.useState<boolean>(false)

	const uri = useAtomValue(qrUriAtom)
	const urlRef = React.useRef<HTMLDivElement>(null)

	const qrLandingUrl = uri ? buildQrLandingUrl(uri) : ''

	const onCopy = async () => {
		if (!qrLandingUrl) return

		if (!(await copyToClipboard(qrLandingUrl))) {
			selectElementContents(urlRef.current)
			showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
				context: previewMode ? 'generator' : 'default',
			})
			return
		}

		setIsCopied(true)
	}

	const urlPreview = (
		<div
			className={cn(
				inputCls(),
				'relative mt-6 flex items-center [&:not([disabled])]:hover:ring-1',
				previewMode ? 'mt-2 gap-2 px-2' : 'gap-4 px-4',
			)}
		>
			<div
				className={cn(
					'pointer-events-none flex gap-4 text-grey',
					previewMode && 'left-2 gap-2',
				)}
			>
				<LineCopy className="h-5 w-5" />
			</div>
			<div className="h-2/5 border-r border-border-grey-50" />
			<div
				ref={urlRef}
				className={cn(
					'block select-all items-center overflow-hidden text-ellipsis whitespace-nowrap',
					previewMode ? 'type-body-small' : 'type-body-medium',
				)}
			>
				{qrLandingUrl}
			</div>
		</div>
	)

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				setIsOpen(open)
				setIsCopied(false)
			}}
			modal={!previewMode}
		>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				CustomPortal={previewMode ? ShareDialogPortal : undefined}
				className={cn(
					'max-w-[min(350px,calc(100%_-_2rem))] p-6',
					previewMode && 'translate-y-[calc((100%-38px)/(-2))] p-4',
				)}
			>
				<DialogHeader>
					<DialogTitle
						className={cn(previewMode ? 'type-heading-4' : 'type-heading-3')}
					>
						{t('generator__share_qr_dialog__title')}
					</DialogTitle>
					<DialogDescription
						className={cn(
							'mt-2 text-dark-grey',
							previewMode ? 'type-body-caption mt-0' : 'type-body-medium',
						)}
					>
						{t('generator__share_qr_dialog__description')}
					</DialogDescription>
				</DialogHeader>
				<div
					className={cn(
						'mt-6 flex flex-row items-center justify-center gap-4',
						previewMode && 'mt-2 gap-2',
					)}
				>
					<FacebookShareButton
						qrLandingUrl={qrLandingUrl}
						previewMode={previewMode}
					/>
					<LinkedinShareButton
						qrLandingUrl={qrLandingUrl}
						previewMode={previewMode}
					/>
					<XShareButton qrLandingUrl={qrLandingUrl} previewMode={previewMode} />
				</div>
				<p
					className={cn(
						'type-body-medium mt-6 text-center text-dark-grey',
						previewMode && 'type-body-caption mt-2',
					)}
				>
					{t('generator__share_qr_dialog__or')}
				</p>
				{urlPreview}
				<Button
					size={previewMode ? 'default' : 'large'}
					onClick={onCopy}
					className={cn('mt-4 w-full', previewMode && 'mt-2 text-body-medium')}
				>
					{t('generator__share_qr_dialog__copy_button')}
				</Button>
				{isCopied && (
					<span
						className={cn(
							'flex w-full justify-center text-center text-green word-break-fix',
							previewMode ? 'type-body-caption mt-2' : 'type-body-medium mt-6',
						)}
					>
						{t('generator__share_qr_dialog__success_copy_button')}
					</span>
				)}
			</DialogContent>
		</Dialog>
	)
}
